import { useLocation, useHistory } from 'react-router-dom';

type Options = {
  /**
   * When true, use history.replace instead of history.push so the update
   * doesn't create a new browser history entry. Useful for high-frequency
   * writes (e.g. form inputs) where each keystroke shouldn't add to the
   * back-button stack and can also trip the browser's pushState rate limit.
   */
  replace?: boolean;
};

export default function useQueryParams() {
  const location = useLocation();
  const history = useHistory();

  const commit = (queryParams: URLSearchParams, options?: Options) => {
    const next = {
      ...location,
      search: queryParams.toString(),
    };
    if (options?.replace) {
      history.replace(next);
    } else {
      history.push(next);
    }
  };

  const setQueryParam = (key: string, val: string, options?: Options) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(key, val);
    commit(queryParams, options);
  };

  const setQueryParams = (
    params: { [key: string]: string },
    options?: Options
  ) => {
    const queryParams = new URLSearchParams(location.search);
    for (const [key, val] of Object.entries(params)) {
      queryParams.set(key, val);
    }
    commit(queryParams, options);
  };

  const removeQueryParam = (...params: string[]) => {
    const queryParams = new URLSearchParams(location.search);

    for (const key of params) {
      if (queryParams.has(key)) {
        queryParams.delete(key);
      }
    }

    commit(queryParams);
  };

  const getQueryParam = (key: string) => {
    return new URLSearchParams(location.search).get(key);
  };

  return {
    setQueryParam,
    setQueryParams,
    getQueryParam,
    removeQueryParam,
  };
}
